/**
 * PaymentCountdown Component
 * 
 * Real-time countdown timer for approved_pending_payment bookings.
 * Shows time remaining until payment deadline expires.
 * 
 * Features:
 * - Live countdown that updates every second
 * - Visual urgency indicators (color changes as time decreases)
 * - Different messaging for renter vs host
 * - Expired state handling
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Clock, AlertTriangle, XCircle } from 'lucide-react';

const PaymentCountdown = ({ 
  paymentDeadlineInfo, 
  isHost = false,
  onExpired,
  className = '',
  compact = false
}) => {
  const [secondsRemaining, setSecondsRemaining] = useState(
    paymentDeadlineInfo?.seconds_remaining || 0
  );
  const [isExpired, setIsExpired] = useState(
    paymentDeadlineInfo?.is_expired || false
  );

  // Update countdown every second
  useEffect(() => {
    if (!paymentDeadlineInfo || isExpired) return;

    // Initialize from server value
    setSecondsRemaining(paymentDeadlineInfo.seconds_remaining || 0);
    setIsExpired(paymentDeadlineInfo.is_expired || false);

    const interval = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          setIsExpired(true);
          clearInterval(interval);
          if (onExpired) onExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [paymentDeadlineInfo, onExpired]);

  // Format time remaining
  const formatTime = useCallback((totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  }, []);

  // Get urgency level for styling
  const getUrgencyLevel = useCallback(() => {
    if (isExpired) return 'expired';
    if (secondsRemaining <= 300) return 'critical'; // < 5 minutes
    if (secondsRemaining <= 1800) return 'warning'; // < 30 minutes
    if (secondsRemaining <= 3600) return 'caution'; // < 1 hour
    return 'normal';
  }, [secondsRemaining, isExpired]);

  const urgency = getUrgencyLevel();

  // Styling based on urgency
  const styles = {
    expired: {
      container: 'bg-red-100 border-red-300',
      text: 'text-red-700',
      icon: 'text-red-600',
      accent: 'text-red-600'
    },
    critical: {
      container: 'bg-red-50 border-red-300 animate-pulse',
      text: 'text-red-700',
      icon: 'text-red-600',
      accent: 'text-red-600'
    },
    warning: {
      container: 'bg-orange-100 border-orange-300',
      text: 'text-orange-700',
      icon: 'text-orange-600',
      accent: 'text-orange-600'
    },
    caution: {
      container: 'bg-amber-100 border-amber-300',
      text: 'text-amber-700',
      icon: 'text-amber-600',
      accent: 'text-amber-600'
    },
    normal: {
      container: 'bg-blue-50 border-blue-200',
      text: 'text-blue-700',
      icon: 'text-blue-600',
      accent: 'text-blue-600'
    }
  };

  const currentStyle = styles[urgency];

  if (!paymentDeadlineInfo) return null;

  // Compact version for lists/cards
  if (compact) {
    return (
      <div 
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${currentStyle.container} border ${className}`}
        data-testid="payment-countdown-compact"
      >
        {isExpired ? (
          <>
            <XCircle className={`w-3 h-3 ${currentStyle.icon}`} />
            <span className={currentStyle.text}>Expired</span>
          </>
        ) : (
          <>
            <Clock className={`w-3 h-3 ${currentStyle.icon}`} />
            <span className={currentStyle.text}>
              {formatTime(secondsRemaining)}
            </span>
          </>
        )}
      </div>
    );
  }

  // Full version for booking details
  return (
    <div 
      className={`p-4 rounded-lg border-2 ${currentStyle.container} ${className}`}
      data-testid="payment-countdown"
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full ${urgency === 'expired' ? 'bg-red-200' : urgency === 'critical' ? 'bg-red-100' : 'bg-amber-100'}`}>
          {isExpired ? (
            <XCircle className={`w-5 h-5 ${currentStyle.icon}`} />
          ) : urgency === 'critical' ? (
            <AlertTriangle className={`w-5 h-5 ${currentStyle.icon}`} />
          ) : (
            <Clock className={`w-5 h-5 ${currentStyle.icon}`} />
          )}
        </div>
        
        <div className="flex-1">
          {isExpired ? (
            <>
              <h4 className={`font-semibold ${currentStyle.text}`}>
                {isHost ? 'Payment Deadline Passed' : 'Payment Deadline Expired'}
              </h4>
              <p className={`text-sm mt-1 ${currentStyle.accent}`}>
                {isHost 
                  ? 'The renter did not complete payment in time. This approval will expire and dates will be released.'
                  : 'Payment was not completed in time. The booking approval has expired and dates have been released.'
                }
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h4 className={`font-semibold ${currentStyle.text}`}>
                  {isHost ? 'Awaiting Renter Payment' : 'Complete Payment'}
                </h4>
                <span className={`text-lg font-mono font-bold ${currentStyle.accent}`}>
                  {formatTime(secondsRemaining)}
                </span>
              </div>
              <p className={`text-sm mt-1 ${currentStyle.accent}`}>
                {isHost 
                  ? 'Waiting for renter to complete payment. Booking will be confirmed once payment is received.'
                  : 'Complete payment to confirm your booking. If payment is not completed before the deadline, the approval will expire.'
                }
              </p>
              
              {/* Progress bar */}
              <div className="mt-3 h-2 bg-white/50 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-1000 ${
                    urgency === 'critical' ? 'bg-red-500' :
                    urgency === 'warning' ? 'bg-orange-500' :
                    urgency === 'caution' ? 'bg-amber-500' :
                    'bg-blue-500'
                  }`}
                  style={{ 
                    width: `${Math.min(100, (secondsRemaining / (3 * 3600)) * 100)}%` // 3 hours = 100%
                  }}
                />
              </div>
              
              {urgency === 'critical' && !isHost && (
                <p className="text-xs font-semibold text-red-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Less than 5 minutes remaining!
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCountdown;
